import "./DSASchedules.scss";
import {
  CalendarOutlined,
  EditOutlined,
  LeftOutlined,
  LoadingOutlined,
  PlusOutlined,
  SearchOutlined
} from "@ant-design/icons";
import { Button, Drawer, Dropdown, Input, message, Tooltip } from "antd";
import React, { ReactNode, useCallback, useContext, useEffect, useState, useRef } from "react";
import { ManageScheduleModal, ModalMode } from "../dsa/ManageScheduleModal";
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import rrulePlugin from '@fullcalendar/rrule';
import { AppContext } from "../../AppContext";
import FullCalendar from "@fullcalendar/react";
import DSAApi from "../../api/DSAApi";
import LocationsApi from "../../api/LocationsApi";
import QRDisplayUploadsApi from "../../api/QRDisplayUploadsApi";
import { DesignDto, DsaPlaylistDto, DsaScheduleDto, DsaScheduleEventDto, DsaScreenDto } from "../../generated/dashboard-client";
import { DisplayUploadDto } from "../../domain/DisplayUploadDto";
import { displayEventTitle } from "../../service/DsaService";
import { eventWallClock } from "../../service/dsaEventTime";
import { DeleteScheduleModal } from "../dsa/DeleteScheduleModal";
import moment from "moment";
import { SelectScheduleModal } from "../dsa/SelectScheduleModal";
import { useRecoilState, useRecoilValue } from "recoil";
import { locationsState, memberState, mobileState } from "../../state/atoms";
import { Spinner } from "../account/misc/Spinner";
import { EventClickArg } from "@fullcalendar/core";
import { EventPopoverModal } from "../dsa/EventPopoverModal";
import { DeleteScheduleEventModal } from "../dsa/DeleteScheduleEventModal";
import { handleServerError } from "../../util/ErrorHandler";
import PushToScreenMenu from "../dsa/PushToScreenMenu";
import ScheduleCard from "../dsa/ScheduleCard";
import { useOnClickOutside } from "usehooks-ts";
import useFocus from "../../hooks/useFocus";

export type CalendarEvent = {
  id?: string;
  title: string;
  start: string;
  end: string;
  color?: string;
  daysOfWeek?: number[];
  allDay?: boolean;
  startTime?: string;
  endTime?: string;
  groupId?: string;
  duration?: string;
  repeat?: string;
  rrule?: any; 
}

export type PositionElement = {
  x: number;
  y: number;
}

const mobileViews = {
  aside: 'aside',
  calendar: 'calendar'
}

function getDateInFormat(date: Date) {
  const y = date.getFullYear();
  let m = date.getMonth() + 1;
  let d = date.getDate();
  let h = date.getHours();
  let mi = date.getMinutes();
  let se = date.getSeconds();
  return {
    date: `${y}-${m < 10 ? "0" + m : m}-${d < 10 ? "0" + d : d}`,
    time: `${h < 10 ? "0" + h : h}:${mi < 10 ? "0" + mi : mi}:${se < 10 ? "0" + se : se}`
  }
}

interface DSASchedulesProps {
  screens: DsaScreenDto[];
  isMarketingBannerPage: boolean;
}

export default function DSASchedules({ screens, isMarketingBannerPage }: DSASchedulesProps) {
  const member = useRecoilValue(memberState);
  const { location } = useContext(AppContext);
  const [locations] = useRecoilState(locationsState);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [schedules, setSchedules] = useState([] as DsaScheduleDto[]);
  const [events, setEvents] = useState([] as CalendarEvent[]);
  // Live design/upload/playlist names so renamed content shows its current name in events
  const [designs, setDesigns] = useState([] as DesignDto[]);
  const [uploads, setUploads] = useState([] as DisplayUploadDto[]);
  const [playlists, setPlaylists] = useState([] as DsaPlaylistDto[]);
  // Only true once all three lists loaded, so a deleted asset isn't reported mid-load
  const [contentListsLoaded, setContentListsLoaded] = useState(false);
  const [manageModal, setManageModal] = useState(false);
  const [manageModalMode, setManageModalMode] = useState<ModalMode>(ModalMode.CreateSchedule);
  const [reopenManageModal, setReopenManageModal] = useState(false);

  const [schedule, setSchedule] = useState(null as DsaScheduleDto | null);
  const [event, setEvent] = useState(null as DsaScheduleEventDto | null);
  const [selectedEventStart, setSelectedEventStart] = useState({} as moment.Moment);
  const [selectedEventEnd, setSelectedEventEnd] = useState({} as moment.Moment);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [isSelectScheduleModal, setIsSelectScheduleModal] = useState(false);

  const calRef = useRef({} as FullCalendar);
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");

  const mobile = useRecoilValue(mobileState);
  const [mobileView, setMobileView] = useState(mobileViews.aside);

  const [isEventPopoverModal, setEventPopoverModal] = useState(false);
  const [isDeleteScheduleEventModal, setDeleteScheduleEventModal] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({} as PositionElement);

  const pushMenuRef = useRef(null);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushMenuOpened, setPushMenuOpened] = useState(false);


  const [scheduleName, setScheduleName] = useState('');
  const [isRenaming, setRenaming] = useState(false);
  const [renameInputRef, setRenameInputFocus] = useFocus();

  const selectSchedule = useCallback((it: DsaScheduleDto) => {
    setSchedule(it);
    if (mobile) {
      setMobileView(mobileViews.calendar);
    }
  }, [setSchedule, setMobileView, mobile]);

  function getUUID() {
    const length = 12;
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    const charactersLength = characters.length;
    let result = '';
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  const addEventsToCalendar = useCallback((raw: DsaScheduleEventDto[]) => {
    let computed = [] as CalendarEvent[];
    raw.forEach(ev => {
      const isRecurring = ev.repeat && ev.repeat !== 'no-repeat' && ev.rrule;
      // a timezone-agnostic event yields the same wall clock for every viewer; a legacy event still
      // renders in the viewer's timezone
      const wallClock = eventWallClock(ev);
      let duration;
      if (isRecurring) {
        duration = (wallClock.end.diff(wallClock.start) + 1000) / 1000;
        let hours, minutes, seconds;
        hours = Math.floor(duration / 60 / 60);
        minutes = Math.floor(duration / 60) - hours * 60;
        seconds = duration - hours * 60 * 60 - minutes * 60;
        if (minutes.toString().length < 2) {
          minutes = '0' + minutes.toString();
        }
        if (seconds.toString().length < 2) {
          seconds = '0' + seconds.toString();
        }
        duration = `${hours}:${minutes}:${seconds}`;
      }

      const event = isRecurring ? {
        id: ev.id ?? "",
        title: displayEventTitle(ev, designs, uploads, playlists, contentListsLoaded),
        color: ev.color ?? "",
        start: wallClock.start.format('YYYY-MM-DDTHH:mm'),
        end: '',

        repeat: ev.repeat!!,
        rrule: `DTSTART:${wallClock.start.format('YYYYMMDDTHHmmss')}\nRRULE:${ev.rrule}`,
        duration: duration,
        groupId: getUUID(),
      } as CalendarEvent
      : {
        title: displayEventTitle(ev, designs, uploads, playlists, contentListsLoaded),
        color: ev.color ?? "",
        start: wallClock.start.format('YYYY-MM-DDTHH:mm'),
        end: wallClock.end.clone().add(1, 'seconds').format('YYYY-MM-DDTHH:mm'),
        id: ev.id ?? "",
      } as CalendarEvent;

      computed = [...computed, event];
    });

    setEvents(old => [...old, ...computed]);
  }, [setEvents, designs, uploads, playlists, contentListsLoaded]);

  const getSchedulesInLocation = useCallback((s: DsaScheduleDto[]) => {
    const inLocation = s
      .filter(schedule =>
        locations
          .map(loc => loc.internalId)
          .includes(schedule.locationId)
      );

    inLocation.sort((first, second) => Date.parse(second.updatedDate!) - Date.parse(first.updatedDate!));
    return inLocation;
  }, [locations]);

  const setCalendarEvents = useCallback((s: DsaScheduleDto[]) => {
    const inLocation = getSchedulesInLocation(s);

    setEvents([]);
    inLocation.forEach(it => addEventsToCalendar(it.events ?? []));

    return inLocation;
  }, [addEventsToCalendar, getSchedulesInLocation]);

  useEffect(() => {
    if (!schedule || !schedule.id) {
      setCalendarEvents([]);
      return;
    }

    setCalendarEvents([schedule]);
  }, [setCalendarEvents, schedule]);

  // Load the current design/upload/playlist names for the selected schedule's location
  // so events whose referenced content was renamed display the updated name.
  useEffect(() => {
    const locationId = schedule?.locationId;
    if (!locationId) return;

    setContentListsLoaded(false);
    Promise.all([
      LocationsApi.getLocationDesigns(locationId).then(resp => setDesigns(resp.data)),
      QRDisplayUploadsApi.getDisplayUploadsByLocationId(locationId.toString()).then(resp => setUploads(resp.data)),
      DSAApi.getPlaylists().then(resp => setPlaylists(resp.data)),
    ]).then(() => setContentListsLoaded(true)).catch(() => {});
  }, [schedule?.locationId]);

  useEffect(() => {
    DSAApi.getSchedules().then(resp => {
      const inLocation = getSchedulesInLocation(resp.data);
      setSchedules(inLocation);
      const scToEdit = sessionStorage.getItem('edit-schedule');
      if (scToEdit) {
        selectSchedule(inLocation.find(it => it.id === scToEdit)!);
        sessionStorage.removeItem('edit-schedule');
      } else {
        selectSchedule(inLocation[0]);
      }
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (schedule) {
      setScheduleName(schedule.name!)
    }
  }, [schedule])

  function startRenaming() {
    if (!schedule) return;
    if (lockedForUser(schedule)) return;
    setRenaming(true);
    (setRenameInputFocus as () => void)();
  }

  function rename() {
    if (!schedule) return;
    setRenaming(false);
    if (!scheduleName.trim()) {
      setScheduleName(schedule.name!);
      return;
    }
    DSAApi.renameSchedule(schedule.id!, scheduleName).then((resp) => {
      schedules.find(s => s.id === schedule.id)!.name = resp.data.name!;
      setSchedules([...schedules]);
    }).catch((err) => {
      console.error(err);
    })
  }

  function remove(schedule: DsaScheduleDto) {
    setIsDeleteModal(true);
    setSchedule(schedule);
  }

  function edit(schedule: DsaScheduleDto) {
    setSchedule(schedule);
    setManageModalMode(ModalMode.UpdateSchedule);
    setManageModal(true);
  }

  function toggleLock(schedule: DsaScheduleDto) {
    DSAApi.toggleLockSchedule(schedule.id!).then(resp => {
      const thisSched = schedules.find(sc => sc.id === schedule.id)!;
      thisSched.locked = resp.data.locked;
      setSchedules([...schedules]);
    })
  }

  function lockedForUser(schedule: DsaScheduleDto) {
    return schedule && schedule.locked ? !member.owner : false;
  }

  function createSchedule() {
    setSchedule(null);
    setEvent(null);
    setStartDate(moment().format('YYYY-MM-DD'));
    setStartTime('08:30');
    setEndDate(moment().format('YYYY-MM-DD'));
    setEndTime('09:29:59');
    setManageModalMode(ModalMode.CreateSchedule);
    setManageModal(true);
  }

  function createEvent(schedule: DsaScheduleDto) {
    if (lockedForUser(schedule!)) return;
    setSchedule(schedule);
    setEvent(null);
    setStartDate(moment().format('YYYY-MM-DD'));
    setStartTime('08:30');
    setEndDate(moment().format('YYYY-MM-DD'));
    setEndTime('09:29:59');
    setManageModalMode(ModalMode.CreateEvent);
    setManageModal(true);
  }

  function onDeleteModalClose(deleted: boolean) {
    setIsDeleteModal(false);
    setSchedule(null);
    if (deleted) setSchedules(schedules.filter(it => it.id !== schedule?.id));
  }

  function onUpdated(s: DsaScheduleDto | null, getScheds: boolean) {
    if (s && !schedules.find(it => it.id === s.id)) {
      setSchedules([...schedules, s]);
      addEventsToCalendar(s.events ?? [])
      selectSchedule(s);
    }
    if (getScheds) {
      DSAApi.getSchedules().then(resp => {
        const inLocation = getSchedulesInLocation(resp.data);
        setSchedules([...inLocation]);
        selectSchedule(inLocation.filter(it => it.id === s?.id)[0]);
      })
    }
  }

  function onEventClick(info: EventClickArg) {
    if (lockedForUser(schedule!)) return;
    const thisEvent = schedule?.events?.find(e => e.id === info.event.id)!
    setEvent(thisEvent);
    setSelectedEventStart(moment(info.event.start));
    setSelectedEventEnd(moment(info.event.end));
    const targetXPosition = info.el.getBoundingClientRect().x;
    setPopoverPosition({ x: targetXPosition, y: info.jsEvent.pageY });
    setEventPopoverModal(true);
  }

  function onDateClick() {
  }

  function onSelection(info: any) {
    if (!schedule) {
      setIsSelectScheduleModal(true);
      return;
    }
    if (lockedForUser(schedule)) return;

    const { start, end } = info;
    let s = getDateInFormat(start);
    setStartDate(s.date);
    setStartTime(s.time);

    let e = getDateInFormat(new Date(end-1000));
    setEndDate(e.date);
    setEndTime(e.time);

    setEvent(null);
    setManageModalMode(ModalMode.CreateEvent);
    setManageModal(true);
  }

  function onManageScheduleModalClose() {
    setStartTime("");
    setStartDate("");
    setEndTime("");
    setEndDate("");
    setManageModal(false);
  }

  function onDeleteEventClick() {
    setEventPopoverModal(false);
    setDeleteScheduleEventModal(true);
  }

  function onEditEventClick() {
    setEventPopoverModal(false);
    setManageModalMode(ModalMode.UpdateEvent);
    setManageModal(true);
  }

  function onEventPopoverClose() {
    setEvent(null);
    setEventPopoverModal(false);
  }

  function deleteScheduleEvent(eventId: string) {
    DSAApi.deleteScheduleEvent(schedule!.id!, {
      scheduleId: schedule!.id!,
      eventId: eventId,
      updatedByName: member.name!
    }).then(resp => {
      const respSc = resp.data;
      setSchedule(respSc);
      const thisSc = schedules.find(sc => sc.id === respSc.id)!;
      thisSc.events = respSc.events;
      thisSc.updatedByName = respSc.updatedByName;
      setSchedules([...schedules]);

      setEvent(null);
      setDeleteScheduleEventModal(false);

      if (reopenManageModal) {
        setReopenManageModal(false);
        if (respSc.events && respSc.events.length > 0) {
          setManageModalMode(ModalMode.UpdateSchedule);
          setManageModal(true);
        }
      }

      DSAApi.getScheduleRotations(schedule!.id!).then(resp2 => {
        const rotations = resp2.data;
        rotations.forEach(r => {
          if (r.events && r.events.map(e => e.scheduleEventId).includes(eventId)) {
            r.events = r.events.filter(e => e.scheduleEventId !== eventId);
            DSAApi.updateScheduleRotation(r).then(resp3 => {
              console.log(resp3);
            });
          }
        })
      });
    }).catch(err => {
      setEvent(null);
      setDeleteScheduleEventModal(false);
      console.error(err);
    })
  }

  function onDeleteScheduleEventModalClose() {
    setEvent(null);
    setDeleteScheduleEventModal(false);
    if (reopenManageModal) {
      setManageModalMode(ModalMode.UpdateSchedule);
      setManageModal(true);
      setReopenManageModal(false);
    }
  }

  function withTooltip(tooltip: string, object: ReactNode) {
    return <Tooltip placement="bottom" title={tooltip} getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}>
      {object}
    </Tooltip>
  }

  function onPushToScreensClick() {
    if (screens.length === 0) return;
    if (!schedule) return;
    if (screens.length === 1) {
      pushScheduleToScreens(schedule, [screens[0]]);
    } else {
      setPushMenuOpened(true);
    }
  }

  const handleClickOutside = (ev: MouseEvent) => {
    setPushMenuOpened(false);
  }

  useOnClickOutside(pushMenuRef, (ev) => handleClickOutside(ev as MouseEvent));

  function pushScheduleToScreens(schedule: DsaScheduleDto, screens: DsaScreenDto[]) {
    setPushLoading(true);
    screens.forEach(sc => {
      try {
        DSAApi.setScreenContent(sc.serial!, {
          type: "SCHEDULE",
          sourceId: schedule.id,
          playable: true
        }).then(() => {
          message.success(`Your schedule has been successfully pushed to your ${screens.length === 1 ? 'screen' : 'screens'}`);
          setPushLoading(false);
          setPushMenuOpened(false);
        })
      } catch (e) {
        handleServerError(e);
        setPushLoading(false);
      }
    })
  }

  function onClickDeleteScheduleEventFromModal(ev: DsaScheduleEventDto) {
    setManageModal(false);
    setEvent(ev);
    setDeleteScheduleEventModal(true);
    setReopenManageModal(true);
  }

  const pushContent = <div>
    <PushToScreenMenu
      schedule={schedule}
      onSchedulePush={pushScheduleToScreens}
      pushLoading={pushLoading}
      screens={screens}
    />
  </div>;

  return <div className={"dsas"}>
    {loading && <div className={'dsa'}>
      <div className={'spinner'}><Spinner /></div>
    </div>}

    {!loading && <>
      {(!mobile || (mobile && mobileView === mobileViews.aside)) &&
        <aside style={{top: isMarketingBannerPage ? 140 : ''}}>
          <header>
            <h3>Schedules</h3>
            {withTooltip('New Schedule', <div className="new-schedule-btn" onClick={createSchedule}><PlusOutlined/></div>)}
          </header>

          <Input type="search" prefix={<SearchOutlined />} placeholder="Search Schedules" value={search}
            onChange={ev => setSearch(ev.target.value)} />

          {schedules.length > 0 && <h5>Schedule Name</h5>}
          <ul className={"schedules"}>
            {schedules
              .filter(it => (it.name ?? "").toLowerCase().indexOf(search.toLowerCase()) > -1)
              .map(it => <ScheduleCard
                it={it}
                selected={it.id === schedule?.id}
                selectSchedule={selectSchedule}
                edit={edit}
                toggleLock={toggleLock}
                deleteSchedule={remove}
                createEvent={createEvent}
                withTooltip={withTooltip}
              />)}
            {schedules
              .filter(it => (it.name ?? "").toLowerCase().indexOf(search.toLowerCase()) > -1).length === 0 && search &&
            <span className="none-found">No schedules found.</span>}
          </ul>
        </aside>}

      {(!mobile || (mobile && mobileView === mobileViews.calendar)) &&
        <div className={"main"}>
          <header className={!!schedule ? "vis" : ""}>
            {mobile && <Button onClick={() => setMobileView(mobileViews.aside)} className="back-btn">
              <LeftOutlined />
            </Button>}

            {schedule && <>
              {!isRenaming && (
                <div
                  className={`edit-name ${lockedForUser(schedule) ? 'naming-locked' : ''}`}
                  onClick={startRenaming}
                >
                  <h2>{scheduleName}</h2>
                  <EditOutlined />
                </div>
              )}
              {!lockedForUser(schedule) && <div
                className={`editing-name ${isRenaming ? "show" : ""}`}
              >
                <Input
                  ref={renameInputRef}
                  value={scheduleName}
                  onChange={(ev) => setScheduleName(ev.target.value)}
                  onBlur={rename}
                />
              </div>}
            </>}

            <nav>
              {!lockedForUser(schedule!) &&
              <Button className={"grey new-event-btn"} onClick={() => createEvent(schedule!)}>+ New Event</Button>}

              {/*!lockedForUser(schedule!) &&
              <Button className="icon-btn" type={"link"}><img src="https://timber.mhmcdn.com/site/marketing/icons/Duplicate.svg"
              alt="push to screen" /></Button>*/}

              {!mobile && withTooltip(`Push to Screen${screens.length > 1 ? 's' : ''}`,  <Dropdown
                placement="bottomRight"
                overlayClassName="playlist-edit-menu schedule-edit-menu"
                visible={pushMenuOpened}
                overlay={<div ref={pushMenuRef}>
                  {pushContent}
                </div>}
              >
                {screens.length === 1 && pushLoading ? <div className="icon-loading"><LoadingOutlined/></div>
                : <Button className={`icon-btn ${screens.length === 0 || !schedule ? 'disabled' : ''}`} type={"link"} onClick={() => onPushToScreensClick()}>
                  <img src="https://timber.mhmcdn.com/site/marketing/icons/ScreenUpload.svg" alt="push to screen" />
                </Button>}
              </Dropdown>)}

              {mobile && <>
                {screens.length === 1 && pushLoading ? <div className="icon-loading"><LoadingOutlined/></div>
                : <Button type={"link"} className={'icon-btn'} onClick={() => onPushToScreensClick()}>
                  <img src="https://timber.mhmcdn.com/site/marketing/icons/ScreenUpload.svg" alt="push to screen" />
                </Button>}
                <Drawer
                  placement="bottom"
                  visible={pushMenuOpened}
                  height={"80vh"}
                  maskClosable={true}
                  onClose={() => setPushMenuOpened(false)}
                  zIndex={1001}
                  className="mobile-bottom-menu push-to-screens-dropdown playlist-edit-menu schedule-edit-menu"
                >
                  <header className="no-back-btn">
                    <h3>Push to Screens</h3>
                  </header>
                  {pushContent}
                </Drawer>
              </>}
            </nav>
          </header>

          {schedules.length === 0 && <div className={"no-data"}>
            <CalendarOutlined />
            <p>
              You don't have any schedules yet.<br />
              Create a new schedule to get started.
            </p>
            <Button type={"primary"} size={"large"} onClick={createSchedule}>Create a Schedule</Button>
          </div>}

          {schedules.length > 0 &&
            <FullCalendar
              ref={calRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, rrulePlugin]}
              initialView="timeGridWeek"
              allDaySlot={false}
              dayHeaderContent={(args) => {
                const m = moment.utc(args.date);
                return { html: `<div>${m.format('ddd')}</div><div>${m.format('D')}</div>` };
              }}
              headerToolbar={{
                left: "today,prev,next",
                center: mobile ? '' : "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay"
              }}
              events={events}
              selectable={true}
              editable={false}
              eventClick={onEventClick}
              dateClick={onDateClick}
              select={onSelection}
              selectLongPressDelay={1000}
              height={mobile ? 'calc(100vh - 200px)' : '90%'}
              nowIndicator={true}
            />}
        </div>}
    </>}

    <DeleteScheduleModal schedule={schedule} isVisible={isDeleteModal} onClose={onDeleteModalClose} />

    <SelectScheduleModal isVisible={isSelectScheduleModal} onClose={() => setIsSelectScheduleModal(false)} />

    <ManageScheduleModal
      isVisible={manageModal}
      mode={manageModalMode}
      setMode={setManageModalMode}
      onModalClose={onManageScheduleModalClose}
      onUpdated={onUpdated}
      schedule={schedule}
      events={events}
      event={event}
      setEvent={setEvent}
      locationId={location.internalId ?? 0}
      initStartDate={startDate}
      initStartTime={startTime}
      initEndDate={endDate}
      initEndTime={endTime}
      deleteEvent={onClickDeleteScheduleEventFromModal}
    />

    <EventPopoverModal
      event={event!}
      title={event ? displayEventTitle(event, designs, uploads, playlists, contentListsLoaded) : ""}
      eventStart={selectedEventStart}
      eventEnd={selectedEventEnd}
      isVisible={isEventPopoverModal}
      position={popoverPosition}
      onDeleteClick={() => onDeleteEventClick()}
      onEditClick={() => onEditEventClick()}
      onClose={() => onEventPopoverClose()}
      withTooltip={withTooltip}
    />

    <DeleteScheduleEventModal
      isVisible={isDeleteScheduleEventModal}
      event={event!}
      deleteEvent={deleteScheduleEvent}
      onClose={() => onDeleteScheduleEventModalClose()}
    />
  </div>
}
